/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Observable, of, throwError as observableThrowError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

// Models
import { GcpUploadResponse } from '@core/models';
import { MongoUser, USER_TYPES } from '@core/models/mongo';
// Services
import { ApiService } from 'app/services/api.service';
import { AuthService } from '@services/auth.service';
import { ClusterService } from './cluster.service';
import { IApiResponse, SuperAdminUser } from '@truckstop-tms/zuum-lib';

export enum APP_TYPE {
  CARRIER = 'carrier',
  CARRIER_TMS = 'carrier-tms',
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private authService: AuthService,
    private clusterService: ClusterService
  ) {}

  create(user: MongoUser, exportCompanyToES = true): Observable<IApiResponse> {
    return (
      this.http
        // For super-admin-web no invites are sent via email when creating any user
        .post<IApiResponse>(
          `${this.apiService.users}/user?sendInvite=false&exportCompanyToES=${exportCompanyToES ? 'true' : 'false'}`,
          user
        )
        .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)))
    );
  }

  createUserWithCompany(user: MongoUser): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/user-with-company`, user)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  deleteById(id: string | undefined): Observable<any> {
    if (!id) {
      return of(null);
    }
    return this.http
      .delete<IApiResponse>(`${this.apiService.users}/${id}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getById(userId: string): Observable<IApiResponse<SuperAdminUser>> {
    return this.http
      .get<IApiResponse>(`${this.apiService.users}/${userId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getUsersOfCompany(compId: string): Observable<IApiResponse<SuperAdminUser[]>> {
    return this.http
      .get<IApiResponse>(`${this.apiService.users}/company/${compId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateMongoUser(user: Partial<MongoUser>): Observable<IApiResponse> {
    const { _id: removed, ...updatedUser } = user;
    return this.http
      .put<IApiResponse>(`${this.apiService.users}/${user._id}`, updatedUser)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getAllClientUsersBranchAgencyIncluded(clientId: number): Observable<IApiResponse> {
    return this.http
      .get<IApiResponse>(`${this.apiService.superAdminApi}/users/${clientId}/branch-agency-included`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateProfilePicture(image: File): Observable<IApiResponse<GcpUploadResponse>> {
    const formData: FormData = new FormData();
    formData.append('file', image);
    return this.http
      .post<any>(`${this.apiService.storageApi}/upload-file`, formData)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  approveById(userId: string): Observable<IApiResponse> {
    return this.http
      .put<IApiResponse>(`${this.apiService.users}/${userId}/approve`, {})
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  activateById(userId: string): Observable<IApiResponse> {
    return this.http
      .put<IApiResponse>(`${this.apiService.users}/${userId}/activate`, {})
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  deactivateById(userId: string): Observable<IApiResponse> {
    return this.http
      .put<IApiResponse>(`${this.apiService.users}/${userId}/deactivate`, {})
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  search(params: any): Observable<IApiResponse> {
    params['tenant_id'] = this.authService.authUser.company.tenantId;
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/search`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  searchWithES(params: any): Observable<IApiResponse> {
    const body = { ...params, isDeleted: false };
    return this.http
      .post<IApiResponse>(`${this.apiService.esAPI}/search/users`, body)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  // check Email
  checkEmail(params: any): Observable<IApiResponse> {
    return this.http.post<IApiResponse>(`${this.apiService.users}/check-email`, params);
  }

  // check Email
  checkEmailSuperAdmin(email: string, types?: USER_TYPES[]): Observable<IApiResponse> {
    let queryParams = '';
    types?.forEach((t) => (queryParams += `types[]=${t}&`));
    queryParams = queryParams.slice(0, -1);

    return this.http.get<IApiResponse>(
      `${this.apiService.superAdminApi}/users/email/${email}${queryParams ? `?${queryParams}` : ''}`
    );
  }

  checkBrokerEmail(params: { email: string; type: string }): Observable<IApiResponse> {
    return this.http.post<IApiResponse>(`${this.apiService.superAdminApi}/users/check`, params);
  }

  getUserByTenantIdTogetherWithSuperAdminUsers(tenantId: string): Observable<IApiResponse<Partial<SuperAdminUser>[]>> {
    return this.http
      .get<IApiResponse<Partial<SuperAdminUser>[]>>(`${this.apiService.superAdminApi}/users/by-tenant-id/${tenantId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  checkPhoneNumber(params): Observable<IApiResponse> {
    return this.http.post<IApiResponse>(`${this.apiService.users}/check-phoneNumber`, params);
  }

  inviteBroker(email: string, tenantId: string): Observable<IApiResponse> {
    const host = this.clusterService.currentCluster?.adminUrl;
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/invite-broker`, {
        email,
        tenantId,
        host: host.replace(/(^\w+:|^)\/\//, ''),
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  inviteShipper(email: string, tenantId: string): Observable<IApiResponse> {
    const host = this.clusterService.currentCluster?.shipperUrl;
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/invite-shipper/${email}`, {
        tenantId,
        host: host.replace(/(^\w+:|^)\/\//, ''),
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  inviteCarrier(email: string, tenantId: string): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/saas-invite-carrier`, { email, tenantId })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  inviteCarrierTmsUser(userId: string): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.coreApi}/v2/users/${userId}/invite-carrier-tms-user/super-admin`, {
        userType: USER_TYPES.FLEET_MANAGER,
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  inviteSuperAdmin(email: string, tenantId: string): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/invite-super-admin`, {
        email,
        tenantId,
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  inviteDriver(phoneNumber: string, tenantId?: string): Observable<IApiResponse> {
    return this.http
      .post<IApiResponse>(`${this.apiService.users}/welcome-sms-carrier`, {
        phoneNumber,
        tenantId,
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getBilledUserForCurrentMonth(companyId: string): Observable<IApiResponse> {
    return this.http
      .get<IApiResponse>(`${this.apiService.coreApi}/users/company/${companyId}/billed`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }
}
